import { usePublicPlans, type PublicPlanCard } from "@/api/queries/publicPlans";
import { pickI18nBlob } from "@/components/admin/I18nField";
import { entitlementAvailability } from "@/lib/entitlementAvailability";

/**
 * Plan facts the marketing copy quotes outside the pricing page (KAN-1944).
 *
 * The landing and the sign-up form used to say "no monthly fee" and "your
 * domain" in prose while the pricing page said something narrower ("custom
 * domain from Pro", "commission X %"). Both now read the same
 * ``GET /public/plans`` the pricing page renders, so a repricing in
 * ``/admin/plans`` moves every page at once.
 *
 * ``null`` means "not known yet" (loading, error, empty catalogue): callers
 * fall back to wording that names no number and no tier.
 */

/** The cheapest tier with no subscription — the one "no monthly fee" means. */
export function freeEntryPlan(
  plans: readonly PublicPlanCard[],
): PublicPlanCard | null {
  return (
    [...plans]
      .filter((p) => p.price_monthly_cents === 0)
      .sort((a, b) => a.sort_order - b.sort_order)[0] ?? null
  );
}

export function useFreeEntryPlan(): PublicPlanCard | null {
  const plans = usePublicPlans();
  return freeEntryPlan(plans.data?.items ?? []);
}

export type EntitlementTier =
  | { kind: "all" }
  | { kind: "from"; plan: string }
  | null;

/** From which tier (by its localized name) a grant is available. */
export function useEntitlementTier(
  entitlement: string,
  lang: string,
): EntitlementTier {
  const plans = usePublicPlans();
  const items = plans.data?.items ?? [];
  if (!items.length) return null;
  const availability = entitlementAvailability(items, entitlement);
  if (availability.kind === "all") return { kind: "all" };
  if (availability.kind === "none") return null;
  const p = availability.plan;
  return { kind: "from", plan: pickI18nBlob(p.name_i18n, p.name, p.name_en, lang) };
}
